import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { AuthField } from "@/components/AuthField";
import { CADGridBackground } from "@/components/CADGridBackground";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { useAuthStore } from "@/store/authStore";

const EASE = [0.22, 1, 0.36, 1] as const;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.09, duration: 0.6, ease: EASE },
  }),
};

export default function LoginScreen() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!EMAIL_RE.test(email)) next.email = "Enter a valid email";
    if (password.length < 6) next.password = "Min. 6 characters";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    await login(email);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div
      data-testid="login-screen"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6 py-12"
    >
      <CADGridBackground opacity={0.28} />
      <div
        aria-hidden
        className="absolute left-1/2 top-[38%] h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.05] blur-3xl"
      />

      <motion.form
        onSubmit={onSubmit}
        noValidate
        initial="hidden"
        animate="show"
        className="relative flex w-full max-w-sm flex-col gap-8"
      >
        <motion.div variants={item} custom={0}>
          <BuildEaseLogo height={40} />
        </motion.div>

        <div className="flex flex-col gap-2">
          <div className="overflow-hidden">
            <motion.h1
              data-testid="login-headline"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
              className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
            >
              Welcome back
            </motion.h1>
          </div>
          <motion.p variants={item} custom={2} className="font-sans text-sm text-white/50">
            Sign in to pick up where your build left off.
          </motion.p>
        </div>

        <div className="flex flex-col gap-4">
          <motion.div variants={item} custom={3}>
            <AuthField
              label="Email"
              index="01"
              testId="login-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@site.com"
              value={email}
              error={errors.email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((p) => ({ ...p, email: undefined }));
              }}
            />
          </motion.div>
          <motion.div variants={item} custom={4}>
            <AuthField
              label="Password"
              index="02"
              testId="login-password-input"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              error={errors.password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((p) => ({ ...p, password: undefined }));
              }}
            />
          </motion.div>
          <motion.div variants={item} custom={5} className="flex justify-end">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="font-sans text-sm text-cad transition-colors duration-200 hover:text-white"
            >
              Forgot password?
            </Link>
          </motion.div>
        </div>

        <motion.div variants={item} custom={6}>
          <motion.button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            whileHover={{ scale: submitting ? 1 : 1.015 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 24 }}
            className="group relative flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl bg-safety font-display text-base font-semibold text-white shadow-[0_12px_40px_-12px_rgba(255,91,0,0.7)] disabled:cursor-wait"
          >
            <span
              aria-hidden
              className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
            />
            <AnimatePresence mode="wait" initial={false}>
              {submitting ? (
                <motion.span
                  key="loading"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]"
                >
                  <Loader2 size={16} className="animate-spin" />
                  Authenticating
                </motion.span>
              ) : (
                <motion.span
                  key="idle"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="flex items-center gap-2"
                >
                  Log In
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </motion.div>

        <motion.p
          variants={item}
          custom={7}
          data-testid="login-signup-text"
          className="text-center font-sans text-sm text-white/50"
        >
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="font-medium text-safety underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
          >
            Sign up
          </Link>
        </motion.p>
      </motion.form>
    </div>
  );
}
